/**
 * Single global URL-change interceptor + subscriber set.
 *
 * Both the V1 store (`apps/oak/src/stores/omnisearchStore.ts`) and the V2 store
 * (`apps/oak/src/hooks/omnisearch-v2/omnisearchStore.ts`) used to install their
 * own copy of these interceptors with their own guard flags. That meant
 * `history.pushState` ended up wrapped twice and each store maintained a
 * separate subscriber set. See REVIEW.md item #3.
 *
 * Centralizing here gives us exactly one install path and one subscriber set.
 * `install` must run before React Router initializes (React Router caches
 * references to `pushState`/`replaceState` at boot) — main.tsx calls it.
 */

const subscribers = new Set<() => void>();

let interceptorsInstalled = false;
let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;

// The last omnisearch value we notified about. Used to deduplicate
// notifications when both `history.replaceState()` and React Router's
// `navigate()` fire for the same URL change.
let lastNotifiedOmnisearch: string | null = null;

function notifySubscribers(): void {
  const params = new URLSearchParams(window.location.search);
  const currentOmnisearch = params.get("omnisearch") ?? "";

  if (currentOmnisearch === lastNotifiedOmnisearch) {
    return;
  }

  lastNotifiedOmnisearch = currentOmnisearch;
  subscribers.forEach((callback) => callback());
}

/**
 * Install push/replace/popstate interceptors. Idempotent.
 *
 * IMPORTANT: Call this from main.tsx BEFORE createBrowserRouter(). React
 * Router caches `history.pushState`/`replaceState` references at boot, so
 * installing afterward causes navigate() to bypass our interceptors.
 */
export function installHistoryInterceptors(): void {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args) => {
    originalPushState?.(...args);
    notifySubscribers();
  };

  history.replaceState = (...args) => {
    originalReplaceState?.(...args);
    notifySubscribers();
  };

  window.addEventListener("popstate", notifySubscribers);
}

/**
 * Subscribe to URL changes. Used by useSyncExternalStore.
 *
 * Calls `installHistoryInterceptors` as a safety net in case main.tsx didn't
 * (e.g. tests). Idempotent.
 */
export function subscribeToURL(callback: () => void): () => void {
  installHistoryInterceptors();
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}
